// ---------------------------------------------------------------------------
// LogoSpinner — animated brand logo used as a loading indicator.
//
// Desktop (pointer: fine — mouse/trackpad):
//   Uses logo-animated-transparent.webm — VP9 with real alpha, smooth.
//
// Mobile / iOS (touch-primary):
//   Uses logo-spinner.apng — APNG with full alpha channel. Renders via <img>
//   which correctly composites alpha on iOS, unlike <video> which overlays
//   a black decode surface regardless of CSS background settings.
//
// Sizes:
//   sm  — w-16   (tight spaces, e.g. inside a card)
//   md  — w-24   (default — inline page loading)
//   lg  — w-32   (full-page auth loading screens)
//   xl  — w-96   (hero / landing page display)
// ---------------------------------------------------------------------------

import { useState, useEffect } from "react";
import headWebm from "@/assets/head-scan-transparent.webm";
import headPng from "@/assets/head.png";

interface LogoSpinnerProps {
  size?: "sm" | "md" | "lg" | "xl";
  label?: string;
  loop?: boolean;
}

const sizeClasses = {
  sm: "w-16",
  md: "w-24",
  lg: "w-32",
  xl: "w-20 sm:w-[20rem]",
};

function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(pointer: fine)").matches;
  });

  useEffect(() => {
    const mq = window.matchMedia("(pointer: fine)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return isDesktop;
}

export default function LogoSpinner({ size = "md", label, loop = true }: LogoSpinnerProps) {
  const isDesktop = useIsDesktop();
  const cls = sizeClasses[size];

  return (
    <div className="flex flex-col items-center gap-3">
      {isDesktop ? (
        <video
          autoPlay
          muted
          loop={loop}
          playsInline
          className={`${cls} h-auto`}
          aria-label={label ?? "Loading"}
        >
          <source src={headWebm} type="video/webm" />
        </video>
      ) : (
        <img
          src={headPng}
          alt={label ?? "Loading"}
          className={`${cls} h-auto`}
        />
      )}
      {label && (
        <p className="text-sm font-medium text-text-secondary">{label}</p>
      )}
    </div>
  );
}
